/**
 * L.6: derive the chat session key from the current user selection.
 *
 * Reads `useActiveContextStore` and returns the session key the chat
 * pane should consume. Rules:
 *
 *   - no selection → `global` scope
 *   - scope=client → `client:{clientId}`
 *   - scope=matter → `matter:{matterId}` (w/ client chain)
 *   - scope=case   → `case:{caseId}` (w/ full chain)
 *
 * **Operator rebrief 2026-06-28:** the viewer is display-only — opening
 * a file in the viewer DOES NOT change the chat scope. Previously
 * (Layout Plan §6.1) an open file derived a `document` sub-scope, so
 * opening a file mid-conversation invisibly bifurcated the chat
 * history (the case chat became a different transcript than the
 * doc chat); closing the viewer reverted to case scope and the
 * "doc conversation" appeared to vanish. New contract: the chat
 * stays at the case scope regardless of viewer state, and the
 * agent gets open-file context via the runtime context block (the
 * agent still knows which file the lawyer is looking at) + via
 * the selection-as-context channel when the lawyer highlights
 * text. The `document` session shape is preserved in the parser
 * (`types/session.ts:parseLxSessionKey`) for back-compat with any
 * on-disk doc-scope transcripts from before this change — the SPA
 * just no longer constructs new ones.
 *
 * L.6.1 alignment: emits the canonical 4-level nested format owned by
 * [session-keys.ts in the Lexiun plugin](../../../lexruntime/openclaw/extensions/lexiun/src/session-keys.ts).
 * The plugin's `parseLxSessionKey` rejects any other shape; an
 * unparseable key means the `before_prompt_build` hook skips context
 * injection for that turn. Supplying the full parent chain is what
 * unlocks CLIENT_MODEL / MATTER_MODEL / CASE_MODEL injection.
 *
 * When a selection is missing required ids (e.g., a case-scope
 * selection that lacks `matterId` — which shouldn't happen in
 * practice but defensively handled), we fall back to `global` rather
 * than throwing: `buildLxSessionKey` is strict, so each branch
 * guards with the full chain before calling it.
 */

import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { buildLxSessionKey } from "@/types/session";

const GLOBAL_KEY = buildLxSessionKey({ type: "global" });

export function useDerivedChatSessionKey(): string {
  const selection = useActiveContextStore((s) => s.selection);

  if (!selection) return GLOBAL_KEY;

  const { scope, clientId, matterId, caseId } = selection;

  if (scope === "case" && caseId) {
    // Task D: caseId-ONLY case scope. A flat/unassigned case (null parents) now
    // gets its own case-scoped thread instead of falling through to GLOBAL_KEY
    // (audit-M2 fix — previously the `clientId && matterId` guard collided every
    // unassigned case's chat onto the global thread).
    return buildLxSessionKey({ type: "case", caseId });
  }

  if (scope === "matter" && clientId && matterId) {
    return buildLxSessionKey({ type: "matter", clientId, matterId });
  }

  if (scope === "client" && clientId) {
    return buildLxSessionKey({ type: "client", clientId });
  }

  return GLOBAL_KEY;
}
